import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { usePublishedJobs } from "@/hooks/useJobPosts";
import { useSavedJobs } from "@/hooks/useSavedJobs";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Clock, X, ArrowUpDown, ChevronLeft, ChevronRight } from "lucide-react";
import { useMemo, useCallback, useState } from "react";
import {
  isCrawlerJob,
  getCompanyName,
  getSourceName,
} from "@/lib/jobUtils";
import { BASE_URL } from "@/lib/constants";
import JobCard from "@/components/jobs/JobCard";
import JobFilters from "@/components/jobs/JobFilters";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const OG_IMAGE = `${BASE_URL}/og-oportunidades.png`;
const JOBS_PER_PAGE = 24;

type SortOption = "recentes" | "salario_desc" | "salario_asc";

const sortLabels: Record<SortOption, string> = {
  recentes: "Mais recentes",
  salario_desc: "Maior bolsa",
  salario_asc: "Menor bolsa",
};

function getInitials(name: string): string {
  const words = name.trim().split(/\s+/);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

function formatRelativeDate(dateStr?: string | null): string | null {
  if (!dateStr) return null;
  const diff = Date.now() - new Date(dateStr).getTime();
  const days = Math.floor(diff / 86_400_000);
  if (days === 0) return "hoje";
  if (days === 1) return "há 1 dia";
  if (days < 30) return `há ${days} dias`;
  const months = Math.floor(days / 30);
  if (months === 1) return "há 1 mês";
  return `há ${months} meses`;
}

export default function Oportunidades() {
  const { data: jobs, isLoading } = usePublishedJobs();
  const { isSaved, toggleSave, user } = useSavedJobs();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sort, setSort] = useState<SortOption>("recentes");

  const search = searchParams.get("q") || "";
  const filterType = searchParams.get("tipo") || null;
  const filterModel = searchParams.get("modelo") || null;
  const filterLocation = searchParams.get("local") || null;
  const filterSource = searchParams.get("fonte") || null;
  const filterSalaryMin = searchParams.get("salario_min") || null;

  const page = parseInt(searchParams.get("p") || "1", 10);

  const updateParam = useCallback(
    (key: string, value: string | null) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (value) {
          next.set(key, value);
        } else {
          next.delete(key);
        }
        // Reset page when changing filters
        if (key !== "p") next.delete("p");
        return next;
      }, { replace: true });
    },
    [setSearchParams]
  );

  const filteredJobs = useMemo(() => {
    if (!jobs) return [];
    const searchLower = search.toLowerCase();

    return jobs.filter((job) => {
      const companyName = getCompanyName(job);
      const matchesSearch =
        !search ||
        job.title.toLowerCase().includes(searchLower) ||
        companyName.toLowerCase().includes(searchLower) ||
        job.location_city?.toLowerCase().includes(searchLower);
      const matchesType = !filterType || job.employment_type === filterType;
      const matchesModel = !filterModel || job.work_model === filterModel;
      const jobLocation = [job.location_city, job.location_state]
        .filter(Boolean)
        .join(", ");
      const matchesLocation = !filterLocation || jobLocation === filterLocation;
      const matchesSource = !filterSource || getSourceName(job) === filterSource;
      const matchesSalary =
        !filterSalaryMin ||
        (job.stipend_max && job.stipend_max >= parseInt(filterSalaryMin));
      return (
        matchesSearch &&
        matchesType &&
        matchesModel &&
        matchesLocation &&
        matchesSource &&
        matchesSalary
      );
    });
  }, [jobs, search, filterType, filterModel, filterLocation, filterSource, filterSalaryMin]);

  const sortedJobs = useMemo(() => {
    const sorted = [...filteredJobs];
    switch (sort) {
      case "salario_desc":
        sorted.sort((a, b) => (b.stipend_max || 0) - (a.stipend_max || 0));
        break;
      case "salario_asc":
        sorted.sort((a, b) => (a.stipend_min || Infinity) - (b.stipend_min || Infinity));
        break;
      case "recentes":
      default:
        // Already ordered by published_at desc from query
        break;
    }
    return sorted;
  }, [filteredJobs, sort]);

  const totalPages = Math.max(1, Math.ceil(sortedJobs.length / JOBS_PER_PAGE));
  const safePage = Math.min(page, totalPages);
  const paginatedJobs = sortedJobs.slice(
    (safePage - 1) * JOBS_PER_PAGE,
    safePage * JOBS_PER_PAGE
  );

  const clearFilters = () => {
    setSearchParams({}, { replace: true });
  };

  const hasActiveFilters =
    filterType || filterModel || filterLocation || search || filterSource || filterSalaryMin;

  const title =
    "Vagas Sem Experiência e Primeiro Emprego | Corre Digital";
  const description =
    "Vagas selecionadas para quem busca o primeiro emprego sem exigir experiência. Cadastre-se grátis e candidate-se agora.";

  return (
    <div className="container px-4 md:px-6 py-8 md:py-12">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={`${BASE_URL}/oportunidades`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${BASE_URL}/oportunidades`} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:site_name" content="Corre Digital Talent" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={OG_IMAGE} />
      </Helmet>

      {/* Hero */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-8"
      >
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-3">
          Vagas de Primeiro Emprego Sem Experiência
        </h1>
        <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
          Vagas selecionadas para quem busca o primeiro emprego sem exigir
          experiência
        </p>
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mb-6"
      >
        <JobFilters
          jobs={jobs}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
        />
      </motion.div>

      {/* Counter + Sort + Clear */}
      <div className="max-w-3xl mx-auto mb-6 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground font-medium">
            {sortedJobs.length} oportunidade
            {sortedJobs.length !== 1 ? "s" : ""} encontrada
            {sortedJobs.length !== 1 ? "s" : ""}
          </span>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
            >
              <X className="h-3 w-3" />
              Limpar filtros
            </button>
          )}
        </div>

        <Select
          value={sort}
          onValueChange={(v) => setSort(v as SortOption)}
        >
          <SelectTrigger className="w-auto h-8 text-xs gap-1">
            <ArrowUpDown className="h-3 w-3" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(sortLabels).map(([key, label]) => (
              <SelectItem key={key} value={key}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Jobs Grid */}
      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <Skeleton className="h-6 w-3/4 mb-3" />
                <Skeleton className="h-4 w-1/2 mb-4" />
                <div className="flex gap-2">
                  <Skeleton className="h-6 w-16" />
                  <Skeleton className="h-6 w-20" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : paginatedJobs.length > 0 ? (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="grid gap-4 md:grid-cols-2 lg:grid-cols-3"
          >
            {paginatedJobs.map((job, index) => (
              <motion.div
                key={job.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.03 * Math.min(index, 10) }}
              >
                <JobCard
                  job={job}
                  isSaved={isSaved(job.id)}
                  onToggleSave={user ? (jobId) => toggleSave.mutate(jobId) : undefined}
                />
              </motion.div>
            ))}
          </motion.div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-8">
              <Button
                variant="outline"
                size="sm"
                disabled={safePage <= 1}
                onClick={() => updateParam("p", String(safePage - 1))}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-muted-foreground px-3">
                {safePage} de {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={safePage >= totalPages}
                onClick={() => updateParam("p", String(safePage + 1))}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </>
      ) : (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-16">
          <Clock className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">
            {hasActiveFilters
              ? "Nenhuma vaga encontrada"
              : "Em breve novas oportunidades"}
          </h3>
          <p className="text-muted-foreground mb-4">
            {hasActiveFilters
              ? "Tente ajustar seus filtros ou buscar com outros termos"
              : "Cadastre-se para ser notificado quando novas vagas forem publicadas"}
          </p>
          {hasActiveFilters && (
            <Button variant="outline" size="sm" onClick={clearFilters}>
              Limpar todos os filtros
            </Button>
          )}
        </motion.div>
      )}

      {/* CTA */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="mt-12 p-6 md:p-8 bg-primary/5 rounded-2xl border border-primary/10 text-center"
      >
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground mb-2">
          Quer se candidatar?
        </h2>
        <p className="text-muted-foreground mb-4">
          Crie seu perfil gratuito no Corre Talent e tenha acesso a todas as
          oportunidades
        </p>
        <Link
          to="/aluno/escolha"
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary text-primary-foreground px-6 py-3 font-semibold hover:bg-primary/90 transition-colors"
        >
          Criar meu perfil grátis
        </Link>
      </motion.div>
    </div>
  );
}
